import type { Density } from "@/hooks/use-list-state"
import { cn } from "@/lib/utils"

function Skeleton({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="skeleton"
      className={cn("animate-pulse rounded-md bg-accent", className)}
      {...props}
    />
  )
}

/* ---- Inco deltas below: composed loading states over the stock base. ----
   Skeletons must sit at the real row heights or they move the jank rather
   than remove it. Heights here mirror the list density scale. */

const ROW_PX: Record<Density, string> = {
  compact: "h-[28px]",
  comfortable: "h-[44px]",
}

export function TableSkeleton({
  rows = 8,
  columns = 5,
  density = "comfortable",
  className,
}: {
  rows?: number
  columns?: number
  density?: Density
  className?: string
}) {
  // Stable per index so the shape does not shimmer between renders.
  const widthFor = (row: number, column: number) =>
    ["w-[70%]", "w-[45%]", "w-[60%]", "w-[35%]", "w-[55%]"][(row + column * 2) % 5]

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading records"
      className={cn("w-full", className)}
    >
      <div className="flex h-[32px] items-center gap-3 border-b px-3">
        {Array.from({ length: columns }).map((_, column) => (
          <Skeleton key={column} className="h-2 w-16 flex-1 rounded-sm opacity-60" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, row) => (
        <div
          key={row}
          className={cn("flex items-center gap-3 border-b px-3", ROW_PX[density])}
        >
          {Array.from({ length: columns }).map((_, column) => (
            <div key={column} className="flex-1">
              <Skeleton className={cn("h-2.5 rounded-sm", widthFor(row, column))} />
              {density === "comfortable" && column === 0 && (
                <Skeleton className="mt-1.5 h-2 w-[35%] rounded-sm opacity-60" />
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

/** Stand-in for a list of rows inside a card — tasks, files, activity. */
export function RowsSkeleton({
  rows = 4,
  className,
}: {
  rows?: number
  className?: string
}) {
  return (
    <div role="status" aria-live="polite" aria-label="Loading" className={className}>
      {Array.from({ length: rows }).map((_, row) => (
        <div key={row} className="flex h-[36px] items-center gap-2.5 border-b px-3 last:border-b-0">
          <Skeleton className="size-4 shrink-0 rounded-full" />
          <Skeleton
            className={cn(
              "h-2.5 rounded-sm",
              ["w-[55%]", "w-[70%]", "w-[40%]", "w-[62%]"][row % 4],
            )}
          />
          <Skeleton className="ml-auto h-2.5 w-12 shrink-0 rounded-sm opacity-60" />
        </div>
      ))}
    </div>
  )
}

export { Skeleton }
